import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  User,
  LogOut,
  X,
  Lock,
  KeyRound,
  BarChart3,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth/useAuth'
import {
  signOut,
  updatePassword,
  passwordProblem,
  recoveryArrival,
} from '@/lib/auth/auth'
import { useRoadStore } from '@/lib/roads/store'
import { useAuthUi } from '@/lib/auth/authUi'

export default function AccountPanel() {
  const { session, profile, ready } = useAuth()
  const openAuthModal = useAuthUi((s) => s.openAuthModal)
  const dismissOverlays = useRoadStore((s) => s.dismissOverlays)
  const openStats = useRoadStore((s) => s.openStats)
  const mapClickSeq = useRoadStore((s) => s.mapClickSeq)

  const [open, setOpen] = useState(false)

  // Change-password section: 1st press opens the fields, press with empty
  // fields collapses them, press with filled fields runs the checks + change.
  const [pwOpen, setPwOpen] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwError, setPwError] = useState<string | null>(null)
  const [pwDone, setPwDone] = useState(false)
  const [pwHighlighted, setPwHighlighted] = useState(false)
  const firstPwInputRef = useRef<HTMLInputElement>(null)

  const pwFilled = newPassword.length > 0 || confirmPassword.length > 0



  async function handleChangePassword() {
    if (!pwOpen) {
      setPwOpen(true)
      return
    }
    if (!pwFilled) {
      setPwOpen(false)
      setPwHighlighted(false)
      setPwError(null)
      return
    }
    const problem = passwordProblem(newPassword)
    if (problem) {
      setPwError(problem)
      return
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match.')
      return
    }
    setPwBusy(true)
    setPwError(null)
    const { error: err } = await updatePassword(newPassword)
    setPwBusy(false)
    if (err) {
      setPwError(err)
      return
    }
    setPwDone(true)
    setNewPassword('')
    setConfirmPassword('')
    setTimeout(() => {
      setPwDone(false)
      setPwOpen(false)
      setPwHighlighted(false)
    }, 3000)
  }

  const signedIn = ready && !!session
  const displayName = profile?.username || profile?.email?.split('@')[0] || 'Rider'

  // Arriving from a recovery email link: jump straight into the change-password
  // flow — auto-open the account dropdown with the password fields expanded,
  // highlighted, and focused once the session lands.
  useEffect(() => {
    if (!recoveryArrival || !signedIn) return
    setOpen(true)
    setPwOpen(true)
    setPwHighlighted(true)
    const t = setTimeout(() => firstPwInputRef.current?.focus(), 350)
    return () => clearTimeout(t)
  }, [recoveryArrival, signedIn])

  // Tapping the map closes the account dropdown (but NOT when the tap was on
  // this dropdown itself — map clicks never fire there, only on the canvas).
  const openRef = useRef(open)
  openRef.current = open
  useEffect(() => {
    if (!open) return
    if (openRef.current) setOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapClickSeq])

  async function toggle() {
    const next = !open
    setOpen(next)
    // Opening the account dropdown (or the sign-in button) should clear the
    // map: close other panels and deselect the selected road.
    if (next) dismissOverlays()
  }

  // Signed out: render a plain "Sign In" button instead of the account dropdown.
  if (ready && !signedIn) {
    return (
      <button
        onClick={() => {
          dismissOverlays()
          openAuthModal('panel')
        }}
        className="h-11 px-3 rounded-lg bg-black/70 border-2 border-white backdrop-blur hover:border-amber-400 transition-colors flex items-center gap-2 text-sm text-white/80"
        title="Sign in or create an account"
      >
        <User size={16} className="text-amber-400" />
        <span className="hidden lg:inline uppercase tracking-wider text-xs">Sign In</span>
      </button>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={toggle}
        className="h-11 px-3 rounded-lg bg-black/70 border-2 border-white backdrop-blur hover:border-amber-400 transition-colors flex items-center gap-2 text-sm text-white/80"
        title={signedIn ? `Signed in as ${displayName}` : 'Account'}
      >
        <User size={16} className={signedIn ? 'text-amber-400' : 'text-white/50'} />
        <span className="hidden lg:inline uppercase tracking-wider text-xs max-w-[10rem] truncate">
          {signedIn ? displayName : 'Account'}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-[calc(100%+8px)] w-80 rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-white/10 p-4 space-y-3 z-50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                  Account
                </div>
                <div className="text-sm font-semibold text-white truncate">
                  {signedIn ? displayName : 'Not signed in'}
                </div>
                {signedIn && profile?.email && (
                  <div className="text-[11px] text-white/45 truncate">{profile.email}</div>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded-lg p-1 text-white/40 hover:bg-white/10 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            {!signedIn && (
              <p className="text-xs text-white/55 leading-relaxed">
                Create a free account to save your routes and pick them back up on any device.
              </p>
            )}

            {signedIn && (
              <>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setOpen(false)
                      openStats(profile?.username ?? displayName)
                    }}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-xs uppercase tracking-wider text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <BarChart3 size={13} />
                    My stats
                  </button>

                  <button
                    onClick={() => void handleChangePassword()}
                    disabled={pwBusy}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-xs uppercase tracking-wider text-white/70 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <KeyRound size={13} />
                    {pwBusy ? 'Updating\u2026' : pwDone ? 'Password updated' : 'Change password'}
                  </button>

                  <AnimatePresence>
                    {pwOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 pt-1">
                          <div className="relative">
                            <Lock
                              size={12}
                              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35"
                            />
                            <input
                              ref={firstPwInputRef}
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="New password"
                              autoComplete="new-password"
                              className={cn(
                                'w-full rounded-lg bg-black/50 border pl-8 pr-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none transition-colors',
                                pwHighlighted
                                  ? 'border-amber-400/70 ring-1 ring-amber-400/40'
                                  : 'border-white/15 focus:border-amber-400/60',
                              )}
                            />
                          </div>
                          <div className="relative">
                            <Lock
                              size={12}
                              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35"
                            />
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void handleChangePassword()
                              }}
                              placeholder="Confirm new password"
                              autoComplete="new-password"
                              className={cn(
                                'w-full rounded-lg bg-black/50 border pl-8 pr-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none transition-colors',
                                pwHighlighted
                                  ? 'border-amber-400/70 ring-1 ring-amber-400/40'
                                  : 'border-white/15 focus:border-amber-400/60',
                              )}
                            />
                          </div>
                          {pwError && (
                            <div className="flex items-start gap-1.5 text-[10px] text-red-400">
                              <AlertCircle size={11} className="mt-px shrink-0" />
                              <span>{pwError}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={() => {
                    void signOut()
                    setOpen(false)
                  }}
                  className="w-full flex items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-xs uppercase tracking-wider text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <LogOut size={13} /> Sign out
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
